import restApi from "@plannel/services/utils/rest-api";

/**
 * 회의 안건 API.
 *
 * 목록은 GET 이 아니라 POST 다 — 검색 조건이 세 축(텍스트·회의시간·작성시간) + 상태 배열 +
 * 태그 배열이라 쿼리스트링에 담기에 길고, 리포의 다른 목록 API 도 같은 방식이다.
 */

/** @param {object} search MeetingAgendaSearchDto 형태. page 는 0 기준 */
const list = (search) => restApi.post("/api/meeting-agendas", search);

const get = (id) => restApi.get(`/api/meeting-agenda/${id}`);

/** id 가 없으면 생성, 있으면 수정 */
const upsert = (agenda) => restApi.post("/api/meeting-agenda/upsert", agenda);

const publish = (id) => restApi.post(`/api/meeting-agenda/publish/${id}`);

const remove = (id) => restApi.post(`/api/meeting-agenda/delete/${id}`);

/** 응답 result 는 토글 후 상태(boolean) */
const toggleStar = (id) => restApi.post(`/api/meeting-agenda/star/${id}`);

/** 드롭다운용. { predefined, used } 두 묶음으로 온다 */
const tags = () => restApi.get("/api/meeting-agenda/tags");

/**
 * 담당자로 고를 수 있는 사람.
 *
 * 공용 /users 는 ADMIN 전용이라 못 쓴다. 이 API 는 이름·부서만 주고 인증된 사용자면 누구나
 * 쓸 수 있다. 서버가 50건에서 끊으므로 keyword 를 같이 보내 좁힌다.
 */
const assignableUsers = (keyword) =>
  restApi.get("/api/meeting-agenda/assignable-users", { params: { keyword: keyword ?? "" } });

/** 담당자 목록에 같이 놓는 직책. 인원 수를 같이 준다 */
const assignablePositions = (keyword) =>
  restApi.get("/api/meeting-agenda/assignable-positions", { params: { keyword: keyword ?? "" } });

/** 직책 하나에 속한 사람들. 직책을 고르면 이걸 받아 담당자로 펼친다 */
const positionUsers = (postnId) => restApi.get(`/api/meeting-agenda/position-users/${postnId}`);

/*
 * 파일 전송.
 *
 * Content-Type 에 boundary 를 직접 쓸 수 없지만, axios 는 FormData 를 감지하면 이 헤더를
 * 비워 브라우저가 boundary 를 붙이게 한다(fp-api-util · data-load 와 같은 패턴).
 */
const MULTIPART = { headers: { "Content-Type": "multipart/form-data" } };

/** 첨부 올리기. 여러 건을 한 번에 보낸다. 응답 result 는 갱신된 첨부 목록 */
const attachFiles = (agendaId, files) => {
  const form = new FormData();
  Array.from(files).forEach((file) => form.append("files", file));
  return restApi.post(`/api/meeting-agenda/files/${agendaId}`, form, MULTIPART);
};

/** 첨부 떼기. linkId 는 z_meeting_agenda_file.id (파일 자체는 남는다) */
const detachFile = (linkId) => restApi.post(`/api/meeting-agenda/file/delete/${linkId}`);

/** 본문에 넣을 이미지. 응답 result 는 <img src> 에 넣을 경로 */
const uploadInlineImage = (agendaId, file) => {
  const form = new FormData();
  form.append("file", file);
  return restApi.post(`/api/meeting-agenda/image/${agendaId}`, form, MULTIPART);
};

/**
 * 첨부 내려받기.
 *
 * <p><b>&lt;a href&gt; 로 걸면 안 된다.</b> 이 앱은 JWT 를 Authorization 헤더로만 받는데
 * (쿠키 인증은 /guide/** 한정), 브라우저가 링크를 직접 따라갈 때는 그 헤더가 실리지 않는다.
 * 그러면 401 이 돌아오고 크롬은 그것을 "사이트에서 사용할 수 없는 파일" 로 표시한다.
 * axios 로 받아서 blob 으로 저장해야 헤더가 붙는다.
 */
const downloadFile = (fileStorageId, fileName) =>
  restApi.get(`/api/meeting-agenda/file/${fileStorageId}`, { responseType: "blob" }).then((res) => {
    /*
     * 받은 것이 파일이 맞는지 <b>먼저 확인한다</b>.
     *
     * 첨부 응답은 어떤 경우에도 HTML 이 아니다. 그런데 요청이 중간에서 새면(로컬 dev 서버의
     * /api 프록시가 가끔 그랬다) 화면 HTML 한 덩어리가 200 으로 돌아오고, 그것을 그대로 저장해
     * 버리면 <b>이름과 확장자는 멀쩡한데 열리지 않는 파일</b>이 남는다. 받는 사람은 원인을
     * 알 길이 없고, 실제로 그 상태로 한참을 헤맸다(2026-09-21).
     *
     * 빈 응답도 같이 막는다. 조용히 0바이트를 저장하느니 실패를 보여 주는 편이 낫다.
     */
    const blob = res.data;
    if (!blob || blob.size === 0 || /text\/html/i.test(blob.type ?? "")) {
      /*
       * 무엇이 왔는지 콘솔에 남긴다. 화면 토스트는 "전송하지 못했습니다" 한 줄이라 원인을
       * 담지 못하는데, 이 실패는 <b>왜</b>가 전부다 — 빈 응답인지 HTML 인지에 따라 볼 곳이
       * 완전히 다르다.
       */
      // eslint-disable-next-line no-console
      console.error("[agenda] attachment download rejected", {
        fileStorageId,
        status: res.status,
        contentType: res.headers?.["content-type"],
        blobType: blob?.type,
        size: blob?.size
      });
      throw new Error(`attachment ${fileStorageId} came back as ${blob?.type || "nothing"} (${blob?.size ?? 0} bytes)`);
    }
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName ?? "download";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    /*
     * blob 주소는 <b>넉넉히 살려 둔다</b>.
     *
     * 클릭은 다운로드를 "요청" 할 뿐이고, 브라우저가 그 blob 을 실제로 읽어 파일에 쓰는 것은
     * 그 뒤에 비동기로 일어난다. 다 읽기 전에 주소를 지우면 이름만 맞고 <b>0바이트인 파일</b>이
     * 남는다 — 받는 사람은 "파일이 안 열린다" 로 겪는다(2026-09-21 실제 사례, 1초였다).
     *
     * 경합이라 파일 크기 · 저장 위치 확인 설정 · 그 순간의 부하에 따라 되기도 하고 안 되기도
     * 한다. 그래서 "몇 초면 충분한가" 를 맞추는 대신 넉넉히 둔다. 주소 하나가 1분 더 살아 있는
     * 비용은 없다시피 하고, 페이지를 떠나면 브라우저가 어차피 정리한다.
     */
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  });

/**
 * 본문 이미지 한 장을 받아 온다.
 *
 * <p>{@code <img src>} 로는 인증 헤더가 실리지 않아 401 이 난다 — 그래서 axios 로 받아
 * 화면에서만 blob 으로 바꿔 끼운다(저장되는 HTML 은 /api 경로 그대로).
 *
 * @param {string} path 본문에 적힌 경로. 예: /api/meeting-agenda/image/42
 */
const inlineImage = (path) => restApi.get(path, { responseType: "blob" });

/** 안건 템플릿. { general, company } 두 묶음으로 온다 */
const templates = () => restApi.get("/api/meeting-agenda/templates");

/** 회사 템플릿을 통째로 갈아 끼운다. 제품 기본은 서버가 들고 있어 건드리지 않는다 */
const saveTemplates = (list) => restApi.post("/api/meeting-agenda/templates", list);

const meetingAgendaService = {
  list, get, upsert, publish, remove, toggleStar, tags,
  assignableUsers, assignablePositions, positionUsers,
  attachFiles, detachFile, uploadInlineImage, downloadFile, inlineImage,
  templates, saveTemplates
};

export default meetingAgendaService;
